import React from "react";

/**
 * JCProgressBar — multi-coloured stacked completion bar used on:
 *   - Job card list rows (compact)
 *   - Job card detail summary header (large)
 *
 * Design (Wave 25.13): orange = pending, blue = in-progress,
 * green = done. [Iter 340 — 2026-02-25] Red segment added for
 * `rejected` tasks so the tech's dashboard shows failed steps at
 * a glance without opening the JC.
 *
 * Props:
 *   tasks: Array<{ status: 'pending'|'in_progress'|'done'|'complete'|'rejected' }>
 *   compact?: boolean  — squashed for list rows
 *   showLabel?: boolean — render trailing "42% complete"
 *   className?: string
 */
export function JCProgressBar({ tasks, compact = false, showLabel = true, className = "" }) {
  const list = Array.isArray(tasks) ? tasks : [];
  const total = list.length;
  if (total === 0) {
    return (
      <div className={`flex items-center gap-2 ${className}`} data-testid="jc-progress-empty">
        <div className={`flex-1 rounded-full bg-white/5 ${compact ? "h-1" : "h-2"}`} />
        {showLabel && (
          <span className={`text-[#647087] tabular-nums ${compact ? "text-[9px]" : "text-[10px]"}`}>
            no tasks
          </span>
        )}
      </div>
    );
  }
  const done = list.filter((t) => t.status === "done" || t.status === "complete").length;
  const inProgress = list.filter((t) => t.status === "in_progress").length;
  const rejected = list.filter((t) => t.status === "rejected").length;
  const pending = total - done - inProgress - rejected;
  // % complete is still "done / total" — rejected tasks are NOT complete
  // even though they're resolved. They surface in the label as a
  // separate ("· 2 rejected") pill so the tech sees the failure count.
  const pct = Math.round((done / total) * 100);
  const donePct = (done / total) * 100;
  const ipPct = (inProgress / total) * 100;
  const rejPct = (rejected / total) * 100;
  const pendPct = (pending / total) * 100;

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="jc-progress">
      <div
        className={`flex-1 rounded-full overflow-hidden bg-white/5 ${compact ? "h-1.5" : "h-2.5"}`}
        title={`${done} done · ${inProgress} in progress · ${rejected} rejected · ${pending} pending`}
      >
        <div className="flex h-full w-full">
          {donePct > 0 && (
            <div
              className="bg-emerald-500/80 transition-[width] duration-300"
              style={{ width: `${donePct}%` }}
              data-testid="jc-progress-done"
            />
          )}
          {ipPct > 0 && (
            <div
              className="bg-sky-500/80 transition-[width] duration-300"
              style={{ width: `${ipPct}%` }}
              data-testid="jc-progress-inprogress"
            />
          )}
          {rejPct > 0 && (
            <div
              className="bg-rose-500/80 transition-[width] duration-300"
              style={{ width: `${rejPct}%` }}
              data-testid="jc-progress-rejected"
            />
          )}
          {pendPct > 0 && (
            <div
              className="bg-amber-500/70 transition-[width] duration-300"
              style={{ width: `${pendPct}%` }}
              data-testid="jc-progress-pending"
            />
          )}
        </div>
      </div>
      {showLabel && (
        <span
          className={`tabular-nums shrink-0 font-mono ${compact ? "text-[9px]" : "text-[10px]"} ${
            pct === 100 ? "text-emerald-300" : pct > 0 ? "text-sky-300" : rejected > 0 ? "text-rose-300" : "text-amber-300"
          }`}
          data-testid="jc-progress-label"
        >
          {pct}% {compact ? "" : "complete"}
          {rejected > 0 && (
            <span className="text-rose-300 ml-1" data-testid="jc-progress-rejected-label">
              · {rejected} rejected
            </span>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * TaskStatusSelector — full-width segmented control for marking a JC task
 * Pending / In progress / Complete / **Rejected**.
 *
 * [Iter 340 — 2026-02-25] Added the Rejected state (red). When the
 * tech picks Rejected the parent MUST prompt for a reason comment —
 * that logic lives in the caller (JobCardDetail) so this component
 * stays a pure segmented control.
 *
 * Big tap targets (44 px+), distinct active colours matching JCProgressBar.
 *
 * Props:
 *   value: current status string
 *   onChange(next): called with the new status
 *   disabled?: boolean
 *   testIdPrefix?: string
 */
export function TaskStatusSelector({ value, onChange, disabled = false, testIdPrefix = "task-status" }) {
  const isDone = value === "done" || value === "complete";
  const isInProgress = value === "in_progress";
  const isRejected = value === "rejected";
  const isPending = !isDone && !isInProgress && !isRejected;

  // Mobile-first: full-width 4-column grid with 44px+ tap targets so techs
  // with wet/dirty fingers can hit the pills reliably. Collapses back to
  // an inline pill row on tablet+ where space allows.
  const baseBtn = "flex-1 min-h-[40px] sm:min-h-0 px-2 sm:px-3 py-2 sm:py-1 text-[11px] sm:text-[11px] font-medium transition-all border-r border-white/10 last:border-r-0 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";

  return (
    <div
      className="flex w-full sm:inline-flex sm:w-auto rounded-full border border-white/10 overflow-hidden bg-black/20 select-none text-xs"
      role="radiogroup"
      aria-label="Task status"
      data-testid={`${testIdPrefix}-group`}
    >
      <button
        type="button"
        onClick={() => onChange("pending")}
        disabled={disabled}
        aria-checked={isPending}
        role="radio"
        className={`${baseBtn} ${
          isPending ? "bg-amber-500/20 text-amber-200" : "text-[#9aa3b5] hover:bg-white/5 active:bg-white/10"
        }`}
        data-testid={`${testIdPrefix}-pending`}
      >
        Pending
      </button>
      <button
        type="button"
        onClick={() => onChange("in_progress")}
        disabled={disabled}
        aria-checked={isInProgress}
        role="radio"
        className={`${baseBtn} ${
          isInProgress ? "bg-sky-500/20 text-sky-200" : "text-[#9aa3b5] hover:bg-white/5 active:bg-white/10"
        }`}
        data-testid={`${testIdPrefix}-in-progress`}
      >
        In&nbsp;progress
      </button>
      <button
        type="button"
        onClick={() => onChange("done")}
        disabled={disabled}
        aria-checked={isDone}
        role="radio"
        className={`${baseBtn} ${
          isDone ? "bg-emerald-500/20 text-emerald-200" : "text-[#9aa3b5] hover:bg-white/5 active:bg-white/10"
        }`}
        data-testid={`${testIdPrefix}-complete`}
      >
        Complete
      </button>
      <button
        type="button"
        onClick={() => onChange("rejected")}
        disabled={disabled}
        aria-checked={isRejected}
        role="radio"
        className={`${baseBtn} ${
          isRejected ? "bg-rose-500/20 text-rose-200" : "text-[#9aa3b5] hover:bg-white/5 active:bg-white/10"
        }`}
        data-testid={`${testIdPrefix}-rejected`}
      >
        Rejected
      </button>
    </div>
  );
}
